import * as THREE from 'three';
import { cloth, emis, leather, metal, std } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { figure, skirt } from './heroes.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The autumn wood over Los Hornos (docs/sendas/druida.md, chapter 3): its people, painted miniatures.
 *
 * - Ruda: Hob's aunt, seventy, a collier: small and a little stooped, a dark wool dress under a charcoal-black apron,
 *   a kerchief knotted over grey hair in a bun, soot on her hands to the wrist; a short clay pipe with its ember.
 * - Elvira: the youngest of the Circle, thirty: slight, a long grey-green cloak with its hood up and her face in its
 *   shadow, her hands held together before her; a sprig of holly at the clasp.
 * - Olmo, young (the memory): a druid of twenty-some, lean; short antlers (two tines each), a cape of green moss over a
 *   brown jerkin, dark hair to the shoulder, no grey; a staff of hazel.
 * - Paz: a young collier woman, her hair tied up in a cloth, sleeves rolled, a leather apron, soot on her forearms; a
 *   felling axe in her hands.
 * - The colliers of then: soot-grey shirts, leather aprons, felt caps or bare heads, an axe each.
 *
 * Colors: wool and leather, soot, moss; never gold.
 */

/** A collier's axe in the right hand: a hickory haft, a grey head. */
const axe = (hand: THREE.Object3D, long = 1): void => {
  const a = G(hand, 0.29, 0.86, 0.14);
  limb(a, V(0, -0.1, 0), V(0, 0.55 * long, 0.04), 0.016, 0.014, std('#6a4a30', { roughness: 0.9 }), 6);
  const head = M(new THREE.BoxGeometry(0.15, 0.09, 0.025), metal('#646a72', { roughness: 0.4 }), a);
  head.position.set(0.06, 0.52 * long, 0.04);
  const edge = M(new THREE.BoxGeometry(0.02, 0.12, 0.028), metal('#a8aeb6', { roughness: 0.25 }), a);
  edge.position.set(0.14, 0.52 * long, 0.04);
};

/** A collier of Los Hornos: `woman`, `cap` (a felt cap), `beard`, `shirt` and `skin` its colours, `old` stooped. */
const collier = (o: BaseOpts | undefined, seed: number, c: { woman?: boolean; cap?: boolean; beard?: boolean; shirt: string; skin: string; hair: string; old?: boolean; tied?: boolean }): Model => {
  const m = begin(o, 0.6, seed);
  const shirt = cloth(c.shirt);
  const soot = std('#2c2b2c', { roughness: 1 });
  const skin = std(c.skin, { roughness: 0.88 });
  const f = figure({ legs: cloth('#34302c'), boots: leather('#1f1914'), torso: shirt, shoulders: shirt, arms: shirt, forearms: c.tied ? soot : shirt, hands: soot, skin });
  m.g.add(f.fig);
  f.fig.scale.set(c.woman ? 0.95 : 1.06, c.woman ? 0.96 : 1, c.woman ? 0.95 : 1.05);
  const b = f.bodyAbs;
  const h = f.headAbs;
  if (c.woman) {
    skirt(f, std('#3a3430', { side: THREE.DoubleSide, roughness: 0.95 }), 0.29, 0.8);
  }
  // The leather apron, scorched at its hem.
  const apron = leather(c.woman ? '#4a3626' : '#3b2b20');
  M(new THREE.BoxGeometry(0.34, 0.7, 0.022), apron, b).position.set(0, 1.0, 0.2);
  M(new THREE.BoxGeometry(0.34, 0.05, 0.024), std('#1a1410', { roughness: 1 }), b).position.set(0, 0.67, 0.21);
  const hair = std(c.hair, { roughness: 1 });
  if (c.tied) {
    // Her hair tied up in a cloth, a strand loose at the temple.
    const wrap = M(new THREE.SphereGeometry(0.14, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.62), cloth('#7a3a2e'), h);
    wrap.position.set(0, 1.665, -0.015);
    wrap.scale.set(1.04, 1, 1.12);
    M(new THREE.SphereGeometry(0.06, 8, 6), cloth('#6a3226'), h).position.set(0, 1.72, -0.12);
    const strand = M(new THREE.CylinderGeometry(0.008, 0.005, 0.12, 4), hair, h);
    strand.position.set(0.1, 1.6, 0.07);
    strand.rotation.z = 0.2;
  } else if (c.cap) {
    const cap = M(new THREE.CylinderGeometry(0.15, 0.145, 0.06, 12), cloth('#2f3033'), h);
    cap.position.set(0, 1.76, 0);
    cap.rotation.x = -0.12;
  } else {
    const top = M(new THREE.SphereGeometry(0.135, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), hair, h);
    top.position.set(0, 1.655, -0.01);
  }
  if (c.beard) {
    const bd = M(new THREE.SphereGeometry(0.1, 10, 6, 0, Math.PI * 2, Math.PI * 0.45, Math.PI * 0.55), c.old ? std('#9a9a96', { roughness: 1 }) : hair, h);
    bd.position.set(0, 1.62, 0.04);
  }
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.011, 5, 4), std('#141214'), h).position.set(k * 0.043, 1.662, 0.114);
    const smear = M(new THREE.SphereGeometry(0.026, 6, 4), soot, h);
    smear.position.set(k * 0.072, 1.635, 0.09);
    smear.scale.set(1, 0.6, 0.4);
  }
  const nose = M(new THREE.ConeGeometry(0.019, 0.048, 5), std(c.skin, { roughness: 0.85 }), h);
  nose.position.set(0, 1.63, 0.13);
  nose.rotation.x = Math.PI / 2 + 0.2;
  axe(f.rhAbs);
  f.rig.armR.rotation.x = -0.3;
  if (c.old) {
    f.rig.body.rotation.x = 0.12;
    f.rig.head.rotation.x = -0.1;
  }
  return done(m, c.woman ? 1.7 : 1.78, () => undefined, f.rig);
};

/** Ruda: the kerchief, the black apron, the clay pipe with its ember. */
const ruda = (o?: BaseOpts): Model => {
  const c = begin(o, 0.58, 921);
  const dress = cloth('#3c3634');
  const soot = std('#252324', { roughness: 1 });
  const skin = std('#c4a090', { roughness: 0.9 });
  const f = figure({ legs: dress, boots: leather('#221b16'), torso: dress, shoulders: cloth('#46403c'), arms: dress, forearms: soot, hands: soot, skin });
  c.g.add(f.fig);
  f.fig.scale.set(0.92, 0.9, 0.92);
  const b = f.bodyAbs;
  const h = f.headAbs;
  skirt(f, std('#3c3634', { side: THREE.DoubleSide, roughness: 0.95 }), 0.3, 0.86);
  // The charcoal-black apron, to the shins, its strings.
  const apron = std('#1c1b1c', { roughness: 0.85 });
  const ap = M(new THREE.BoxGeometry(0.36, 0.78, 0.024), apron, b);
  ap.position.set(0, 0.92, 0.2);
  ring(b, 0.17, 0.016, apron, 0.98, 4, 14);
  // A shawl of grey wool over the shoulders.
  const shawl = cloth('#5a5654');
  for (let i = 0; i < 9; i++) {
    const a = ((i - 4) / 4) * 1.9 + Math.PI;
    const t = M(new THREE.DodecahedronGeometry(0.052, 0), shawl, b);
    t.position.set(Math.sin(a) * 0.19, 1.42 - Math.abs(i - 4) * 0.01, Math.cos(a) * 0.17);
    t.scale.set(1.2, 0.55, 0.8);
  }
  // The kerchief over grey hair, its knot under the chin; the bun at the back.
  const grey = std('#c8c6c2', { roughness: 1 });
  M(new THREE.SphereGeometry(0.135, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.6), grey, h).position.set(0, 1.65, -0.01);
  M(new THREE.SphereGeometry(0.06, 8, 6), grey, h).position.set(0, 1.62, -0.15);
  const kerchief = M(new THREE.SphereGeometry(0.145, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.45), cloth('#5e2a26'), h);
  kerchief.position.set(0, 1.67, -0.02);
  kerchief.scale.set(1.04, 1, 1.12);
  M(new THREE.SphereGeometry(0.025, 6, 4), cloth('#5e2a26'), h).position.set(0, 1.52, 0.09);
  // Deep lines, small dark eyes, a long nose; the clay pipe in the corner of her mouth, its ember.
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.011, 5, 4), std('#141214'), h).position.set(k * 0.042, 1.652, 0.112);
    M(new THREE.BoxGeometry(0.04, 0.008, 0.012), std('#9a7a6c'), h).position.set(k * 0.06, 1.62, 0.1);
  }
  const nose = M(new THREE.ConeGeometry(0.018, 0.055, 5), std('#b8907e', { roughness: 0.85 }), h);
  nose.position.set(0, 1.625, 0.132);
  nose.rotation.x = Math.PI / 2 + 0.25;
  const pipe = G(h, 0.04, 1.585, 0.12);
  limb(pipe, V(0, 0, 0), V(0.1, -0.01, 0.06), 0.006, 0.006, std('#d8d4cc', { roughness: 0.6 }), 4);
  M(new THREE.CylinderGeometry(0.018, 0.014, 0.035, 6), std('#d8d4cc', { roughness: 0.6 }), pipe).position.set(0.105, 0.012, 0.062);
  M(new THREE.SphereGeometry(0.012, 5, 4), emis('#ff7a3a', '#ff5a1a', 2), pipe).position.set(0.105, 0.03, 0.062);
  glowSprite(pipe, '#ff7a3a', 0.06, V(0.105, 0.035, 0.062), 0.7);
  // A little stooped.
  f.rig.body.rotation.x = 0.1;
  f.rig.head.rotation.x = -0.12;
  f.rig.armL.rotation.set(-0.2, 0, 0.2);
  return done(c, 1.55, () => undefined, f.rig);
};

/** Elvira: the cloak with its hood up, her face in its shadow, a sprig of holly at the clasp. */
const elvira = (o?: BaseOpts): Model => {
  const c = begin(o, 0.56, 922);
  const cloak = cloth('#4c5a4c');
  const skin = std('#d4b2a2', { roughness: 0.86 });
  const f = figure({ legs: cloak, boots: leather('#2a231e'), torso: cloak, shoulders: cloak, arms: cloak, forearms: cloak, hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(0.88, 0.97, 0.88);
  const b = f.bodyAbs;
  const h = f.headAbs;
  M(new THREE.ConeGeometry(0.32, 1.0, 12, 1, true), std('#4c5a4c', { side: THREE.DoubleSide, roughness: 0.95 }), f.fig).position.y = 0.5;
  // The hood, up, deep: a dark hollow where the face is, two pale points of eyes in it.
  const hood = M(new THREE.SphereGeometry(0.17, 12, 9, 0, Math.PI * 2, 0, Math.PI * 0.72), cloth('#3e4a3e'), h);
  hood.position.set(0, 1.64, -0.02);
  hood.scale.set(1.0, 1.12, 1.16);
  const hollow = M(new THREE.SphereGeometry(0.11, 10, 7), std('#14161a', { roughness: 1 }), h);
  hollow.position.set(0, 1.62, 0.07);
  hollow.scale.set(0.95, 1.1, 0.5);
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.01, 5, 4), emis('#dfe4ea', '#9aa4b0', 0.6), h).position.set(k * 0.035, 1.64, 0.115);
  M(new THREE.ConeGeometry(0.05, 0.22, 6), cloth('#3e4a3e'), b).position.set(0, 1.42, -0.2);
  // The holly at the clasp: dark leaves, red berries.
  const clasp = G(b, 0, 1.4, 0.18);
  for (let i = 0; i < 3; i++) {
    const l = M(new THREE.ConeGeometry(0.02, 0.07, 4), std('#24402a', { roughness: 0.6 }), clasp);
    l.rotation.z = (i - 1) * 0.9;
    l.scale.z = 0.4;
  }
  for (let i = 0; i < 3; i++) M(new THREE.SphereGeometry(0.012, 5, 4), std('#b8282a', { roughness: 0.4 }), clasp).position.set((i - 1) * 0.015, -0.02, 0.01);
  // Her hands together before her.
  f.rig.armR.rotation.set(-0.6, 0, -0.35);
  f.rig.armL.rotation.set(-0.6, 0, 0.35);
  f.rig.head.rotation.x = 0.15;
  return done(c, 1.72, () => undefined, f.rig);
};

/** Olmo, young: short antlers, a cape of moss, dark hair, a staff of hazel. */
const olmoYoung = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 923);
  const jerkin = leather('#5a4632');
  const moss = std('#5e8a3e', { roughness: 1, flatShading: true });
  const skin = std('#c99a82', { roughness: 0.85 });
  const f = figure({ legs: cloth('#3a3226'), boots: leather('#2a2018'), torso: jerkin, shoulders: cloth('#4a6a36'), arms: cloth('#4e5c3a'), forearms: skin, hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(1.0, 1.02, 1.0);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The cape of moss, from the shoulders to the knees, in tufts.
  const cape = M(new THREE.CylinderGeometry(0.24, 0.34, 0.86, 12, 1, true, Math.PI * 0.6, Math.PI * 1.8), std('#4a6e32', { side: THREE.DoubleSide, roughness: 1 }), b);
  cape.position.set(0, 1.04, -0.02);
  for (let i = 0; i < 16; i++) {
    const a = Math.PI * 0.7 + (i / 15) * Math.PI * 1.6;
    const t = M(new THREE.DodecahedronGeometry(0.055, 0), moss, b);
    t.position.set(Math.cos(a) * 0.26, 1.42 - (i % 4) * 0.2, Math.sin(a) * 0.25 - 0.02);
    t.scale.set(1.1, 0.7, 0.8);
  }
  // Dark hair to the shoulder; a short beard just started.
  const hair = std('#2e2018', { roughness: 1 });
  const top = M(new THREE.SphereGeometry(0.14, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.62), hair, h);
  top.position.set(0, 1.655, -0.015);
  top.scale.set(1.05, 1, 1.1);
  for (const k of [-1, 1]) {
    const lock = M(new THREE.BoxGeometry(0.05, 0.2, 0.08), hair, h);
    lock.position.set(k * 0.11, 1.53, -0.05);
  }
  const stubble = M(new THREE.SphereGeometry(0.095, 10, 6, 0, Math.PI * 2, Math.PI * 0.55, Math.PI * 0.45), std('#3a2a20', { roughness: 1 }), h);
  stubble.position.set(0, 1.62, 0.035);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#18140f'), h).position.set(k * 0.043, 1.658, 0.114);
    M(new THREE.BoxGeometry(0.048, 0.012, 0.014), hair, h).position.set(k * 0.045, 1.686, 0.11);
  }
  const nose = M(new THREE.ConeGeometry(0.018, 0.048, 5), std('#bf8e78', { roughness: 0.85 }), h);
  nose.position.set(0, 1.63, 0.13);
  nose.rotation.x = Math.PI / 2 + 0.2;
  // Short antlers: a beam and two tines each, pale bone, young.
  const bone = std('#d6ccbc', { roughness: 0.7 });
  for (const k of [-1, 1]) {
    const base = V(k * 0.08, 1.75, -0.01);
    const tip = V(k * 0.2, 1.97, -0.04);
    limb(h, base, tip, 0.016, 0.01, bone, 5);
    limb(h, V(k * 0.14, 1.86, -0.02), V(k * 0.12, 1.99, 0.04), 0.01, 0.006, bone, 4);
    limb(h, V(k * 0.18, 1.93, -0.03), V(k * 0.26, 2.02, -0.02), 0.009, 0.005, bone, 4);
  }
  // The staff of hazel, a bud of new leaf at its head.
  const staff = G(f.rhAbs);
  limb(staff, V(0.3, 0.02, 0.12), V(0.31, 1.85, 0.12), 0.022, 0.018, std('#7a5e40', { roughness: 0.95 }), 6);
  M(new THREE.ConeGeometry(0.035, 0.1, 5), std('#8ec060', { roughness: 0.7, emissive: '#2a4410', emissiveIntensity: 0.5 }), staff).position.set(0.31, 1.9, 0.12);
  f.rig.armR.rotation.x = -0.25;
  return done(c, 1.95, () => undefined, f.rig);
};

/** The autumn's people by name (models/sendas.ts sends them here). */
export const otonoModel = (id: string, o?: BaseOpts): Model | null => {
  switch (id) {
    case 'ruda':
      return ruda(o);
    case 'elvira':
      return elvira(o);
    case 'olmoYoung':
      return olmoYoung(o);
    case 'paz':
      return collier(o, 924, { woman: true, tied: true, shirt: '#6a625a', skin: '#d0a48e', hair: '#3a2418' });
    case 'collierA':
      return collier(o, 925, { cap: true, beard: true, shirt: '#4a4b4f', skin: '#b89280', hair: '#2a2220' });
    case 'collierB':
      return collier(o, 926, { woman: true, shirt: '#55504a', skin: '#c79c86', hair: '#4a3426' });
    case 'collierC':
      return collier(o, 927, { beard: true, old: true, shirt: '#46423e', skin: '#b38a78', hair: '#5a5652' });
    default:
      return null;
  }
};
